<script lang="ts" setup>
import LineMinus from '@docue/docue-ui-v2/icons/LineMinus.vue'
import LineInfoCircle from '@docue/docue-ui-v2/icons/LineInfoCircle.vue'
import LineCheckCircleBroken from '@docue/docue-ui-v2/icons/LineCheckCircleBroken.vue'
import DTFieldSelectOption from '@docue/docue-ui-v2/ui-components/DTField/DTFieldSelectOption.vue'

import type { PriceListAddonPrice, PriceListFeature, PriceListFeatureConfigOption, PriceListFeatureItem, PriceListItem } from '~/composables/api/price-list/useFetchPriceList'

interface FeatureOptionType extends PriceListFeatureConfigOption {
  price?: PriceListAddonPrice | undefined
}

const props = defineProps<{
  item: PriceListItem
  feature: PriceListFeature
}>()

const { formatCurrency } = useIntl()
const { t } = useI18n()

const { paymentPeriodUnit, priceList, addons } = useSubscriptionProcessContext()

const isPriceListV1_1 = computed(() => priceList.data.value?.data_version === 'v1.1')

const featureItem = computed(() => {
  if (!isPriceListV1_1.value) {
    return props.item.data.features?.find(({ id }) => id === props.feature.id)
  }

  const features = Array.isArray(props.item.data.features)
    ? props.item.data.features
    : (props.item.data.features as unknown as Record<'year' | 'month', PriceListFeatureItem[]>)?.[paymentPeriodUnit.value]

  return features?.find(({ id }) => id === props.feature.id)
})

const featureTemplateLanguages = computed(() =>
  featureItem.value && featureItem.value.id === 'template-languages' && typeof featureItem.value.value === 'string'
    ? featureItem.value.value.split(',') as App.Internals.Enums.Language[]
    : [],
)

const featureConfig = computed(() => {
  if (!priceList.dataWithoutUpgrades.value?.data?.feature_configs)
    return undefined

  const config = priceList.dataWithoutUpgrades.value.data.feature_configs
    .find(({ feature_id }) => feature_id === props.feature.id)

  if (!config)
    return undefined

  const mappedOptions = config.options.map(({ type, options, ...rest }) => ({
    type,
    options: options.map((opt) => {
      const addon = priceList.dataWithoutUpgrades.value?.addons?.find(({ addon_id }) => addon_id === opt.addon_id)
      if (!addon)
        return opt

      return {
        ...opt,
        price: addon.prices.find(({ period_unit }) => period_unit === paymentPeriodUnit.value),
      }
    }),
    ...rest,
  }))

  return {
    ...config,
    options: mappedOptions,
  }
})

const getOptionPriceText = (option: FeatureOptionType) => {
  if (!('price' in option) || !option.price || !option.quantity)
    return undefined

  return t('subscribe.plans.table.feature-option-price', {
    quantity: option.quantity,
    unitPrice: formatCurrency(option.price.monthly_price * 12 / option.quantity / 100, { minimumFractionDigits: 0 }),
    monthlyPrice: formatCurrency(option.price.monthly_price / 100, { minimumFractionDigits: 0 }),
  })
}

const signatureAddonId = computed(() => addons.value[0]?.addonId ?? null)

const selectedFeatureOptionPriceText = computed(() => {
  if (!featureConfig.value)
    return undefined

  if (signatureAddonId.value === null)
    return featureItem.value?.value

  const selectedOption = featureConfig.value.options.reduce((acc, optGroup) => {
    const opt = optGroup.options
      .find(opt => 'price' in opt && opt.addon_id === signatureAddonId.value) as FeatureOptionType | undefined

    return acc || opt
  }, undefined as FeatureOptionType | undefined)

  if (!selectedOption || !('price' in selectedOption) || !selectedOption.price || !selectedOption.quantity)
    return undefined

  return getOptionPriceText(selectedOption)
})

const FeatureOption = ({ option }: { option: FeatureOptionType }) => {
  const text = computed(() => getOptionPriceText(option) ?? featureItem.value?.value)
  return h(
    DTFieldSelectOption,
    { class: 'text-sm', value: option.addon_id ?? null },
    () => text.value,
  )
}

const updateSignatureAddon = (addonId: string | null) => {
  addons.value = addonId
    ? [{ addonId, quantity: 1 }]
    : addons.value.filter(({ addonId: id }) => id !== signatureAddonId.value)
}
</script>

<template>
  <td
    :class="{
      '!p-0': !!featureConfig,
    }"
  >
    <template v-if="featureConfig && feature.id === 'signing'">
      <DTFieldSelect
        :model-value="signatureAddonId"
        class="w-full !border-0 !bg-transparent"
        @update:model-value="(addonId) => updateSignatureAddon(addonId as string | null)"
      >
        <DTFieldSelectButton size-variant="sm">
          <span class="ml-5 w-full text-center text-gray-800">{{ selectedFeatureOptionPriceText }}</span>
        </DTFieldSelectButton>
        <DTFieldSelectOptions>
          <template
            v-for="(optGroup, optGroupIndex) in featureConfig.options"
            :key="optGroupIndex"
          >
            <div class="p-1 text-xs font-semibold">
              {{ optGroup.label }}
            </div>
            <FeatureOption
              v-for="(option, optIndex) in optGroup.options"
              :key="optIndex"
              :option
            />
          </template>
        </DTFieldSelectOptions>
      </DTFieldSelect>
    </template>

    <div
      v-else-if="featureTemplateLanguages.length"
      class="flex items-center justify-center gap-1"
    >
      <Flag
        v-for="lang in featureTemplateLanguages"
        :key="lang"
        :language="lang"
        :width="16"
        :height="16"
      />
    </div>

    <template v-else-if="typeof featureItem?.value === 'boolean'">
      <component
        :is="featureItem?.value ? LineCheckCircleBroken : LineMinus"
        class="mx-auto size-5"
        :class="featureItem?.value ? 'text-green-500' : 'text-gray-500'"
      />
    </template>
    <span
      v-else
      class="inline-flex items-center gap-1"
    >
      {{ featureItem?.value }}
      <DTTooltip
        v-if="isPriceListV1_1 && featureItem?.description"
        :content="featureItem?.description"
      >
        <LineInfoCircle class="size-3.5 text-gray-500" />
      </DTTooltip>
    </span>
  </td>
</template>
